/**
 * Minimal typed pub/sub emitter for Spaces 3 metrics events.
 *
 * Improves on the Spaces 2 emitter (ui/scripts/v2/emitter.js) by adding
 * "sticky" replay: subscribers to a sticky event receive the most recently
 * emitted payload immediately on subscribe. This lets analytics consumers
 * attach after the app has already mounted and still get the `ready` signal
 * plus the latest list-change state without missing the first emit.
 */

import type {
	SpacesEventEmitter,
	SpacesEventListener,
	SpacesEventMap,
	SpacesEventName
} from '@/types/metrics'

/** Events whose latest payload is replayed to new subscribers. */
const STICKY_EVENTS: ReadonlySet<SpacesEventName> = new Set<SpacesEventName>([
	'ready',
	'metrics.units.change',
	'metrics.plans.change'
])

export function createEmitter (): SpacesEventEmitter {
	const listeners = new Map<SpacesEventName, Set<SpacesEventListener<SpacesEventName>>>()
	const stickyValues = new Map<SpacesEventName, SpacesEventMap[SpacesEventName]>()

	function getListeners<E extends SpacesEventName> (event: E): Set<SpacesEventListener<E>> {
		let set = listeners.get(event)
		if (!set) {
			set = new Set()
			listeners.set(event, set)
		}
		return set as unknown as Set<SpacesEventListener<E>>
	}

	function on<E extends SpacesEventName> (event: E, fn: SpacesEventListener<E>): () => void {
		getListeners(event).add(fn)

		// Replay the latest sticky payload so late subscribers are not starved.
		if (STICKY_EVENTS.has(event) && stickyValues.has(event)) {
			fn(stickyValues.get(event) as SpacesEventMap[E])
		}

		return () => off(event, fn)
	}

	function once<E extends SpacesEventName> (event: E, fn: SpacesEventListener<E>): () => void {
		const wrapped: SpacesEventListener<E> = payload => {
			off(event, wrapped)
			fn(payload)
		}
		return on(event, wrapped)
	}

	function off<E extends SpacesEventName> (event: E, fn: SpacesEventListener<E>): void {
		listeners.get(event)?.delete(fn as SpacesEventListener<SpacesEventName>)
	}

	function emit<E extends SpacesEventName> (event: E, payload: SpacesEventMap[E]): void {
		if (STICKY_EVENTS.has(event)) {
			stickyValues.set(event, payload)
		}

		const set = listeners.get(event)
		if (!set) return
		// Copy to a snapshot so handlers may safely unsubscribe during dispatch.
		for (const fn of Array.from(set)) {
			(fn as SpacesEventListener<E>)(payload)
		}
	}

	return { on, once, off, emit }
}
